<script lang="ts">
import { computed, defineComponent, h, shallowRef, unref, watch } from 'vue';

import { CdsTransitionFade } from '@components/transitions';
import { clamp, classNames, focusableChildren, IN_BROWSER, keyNames } from '@components/utils';
import { CdsFunctionalButton } from '@components/components';

import {
  makeDirectionProps,
  makeDisabledProps,
  makeGroupProps,
  makeIconProps,
  makeSlideGroupProps,
  makeTagProps,
  useDisplay,
  useGroup,
  useRender,
  useResizeObserver
} from '../../composable';

import { bias, calculateCenteredOffset, calculateUpdatedOffset } from './utils';

import type { IGroupProvide } from '../../composable';
import type { TCdsIconsName } from '@central-design-system/icons';
import type { InjectionKey } from 'vue';

export const CdsSlideGroupSymbol: InjectionKey<IGroupProvide> = Symbol.for('cds:slide-group');

export const componentName = 'CdsSlideGroup';
export default defineComponent({
  name: componentName,
  props: {
    ...makeTagProps(),
    ...makeDirectionProps(),
    ...makeDisabledProps(),
    ...makeSlideGroupProps(),
    ...makeGroupProps({
      selectedClass: 'cds-slide-group-item--active'
    }),
    ...makeIconProps({
      prependIcon: 'arrow-left' as TCdsIconsName,
      appendIcon: 'arrow-right' as TCdsIconsName
    }),

    // Internal props
    symbol: {
      type: null,
      default: CdsSlideGroupSymbol
    }
  },
  emits: {
    'update:modelValue': (value: any) => true
  },
  setup(props, { slots }) {
    const { mobile } = useDisplay();
    const group = useGroup(props, props.symbol);
    const isOverflowing = shallowRef(false);
    const scrollOffset = shallowRef(0);
    const containerSize = shallowRef(0);
    const contentSize = shallowRef(0);
    const isHorizontal = computed(() => props.direction === 'horizontal');

    const { resizeRef: containerRef, contentRect: containerRect } = useResizeObserver();
    const { resizeRef: contentRef, contentRect } = useResizeObserver();

    const firstSelectedIndex = computed(() => {
      if (!unref(group.selected).length) return -1;
      return unref(group.items).findIndex((item) => item.id === unref(group.selected)[0]);
    });

    const lastSelectedIndex = computed(() => {
      if (!unref(group.selected).length) return -1;
      return unref(group.items).findIndex(
        (item) => item.id === unref(group.selected)[unref(group.selected).length - 1]
      );
    });

    if (IN_BROWSER) {
      let frame = -1;
      watch(
        () => [unref(group.selected), unref(containerRect), unref(contentRect), unref(isHorizontal)],
        () => {
          cancelAnimationFrame(frame);
          frame = requestAnimationFrame(() => {
            if (unref(containerRect) && unref(contentRect)) {
              const sizeProperty = unref(isHorizontal) ? 'width' : 'height';

              containerSize.value = unref(containerRect)![sizeProperty];
              contentSize.value = unref(contentRect)![sizeProperty];

              isOverflowing.value = unref(containerSize) + 1 < unref(contentSize);
            }

            if (unref(firstSelectedIndex) >= 0 && unref(contentRef)) {
              const selectedElement = unref(contentRef)?.children[unref(lastSelectedIndex)] as HTMLElement;

              if (unref(firstSelectedIndex) === 0 || !unref(isOverflowing)) {
                scrollOffset.value = 0;
              } else if (props.centerActive) {
                scrollOffset.value = calculateCenteredOffset({
                  selectedElement,
                  containerSize: unref(containerSize),
                  contentSize: unref(contentSize),
                  isHorizontal: unref(isHorizontal)
                });
              } else if (unref(isOverflowing)) {
                scrollOffset.value = calculateUpdatedOffset({
                  selectedElement,
                  containerSize: unref(containerSize),
                  contentSize: unref(contentSize),
                  currentScrollOffset: unref(scrollOffset),
                  isHorizontal: unref(isHorizontal)
                });
              }
            }
          });
        }
      );
    }

    const disableTransition = shallowRef(false);

    let startTouch = 0;
    let startOffset = 0;

    function onTouchstart(event: TouchEvent) {
      const sizeProperty = unref(isHorizontal) ? 'clientX' : 'clientY';
      const sign = 1;

      startOffset = sign * unref(scrollOffset);
      startTouch = event.touches[0][sizeProperty];

      disableTransition.value = true;
    }

    function onTouchmove(event: TouchEvent) {
      if (!unref(isOverflowing)) return;

      const sizeProperty = unref(isHorizontal) ? 'clientX' : 'clientY';
      const sign = 1;

      scrollOffset.value = sign * (startOffset + startTouch - event.touches[0][sizeProperty]);
    }

    function onTouchend(event: TouchEvent) {
      const maxScrollOffset = unref(contentSize) - unref(containerSize);

      if (unref(scrollOffset) < 0 || !unref(isOverflowing)) {
        scrollOffset.value = 0;
      } else if (unref(scrollOffset) >= maxScrollOffset) {
        scrollOffset.value = maxScrollOffset;
      }

      disableTransition.value = false;
    }

    function onScroll() {
      if (!containerRef.value) return;

      containerRef.value[unref(isHorizontal) ? 'scrollLeft' : 'scrollTop'] = 0;
    }

    const isFocused = shallowRef(false);
    function onFocusin(event: FocusEvent) {
      isFocused.value = true;

      if (!unref(isOverflowing) || !unref(contentRef)) return;

      // Focused element is likely to be the root of an item, so a
      // breadth-first search will probably find it in the first iteration
      for (const el of event.composedPath()) {
        for (const item of unref(contentRef)!.children) {
          if (item === el) {
            scrollOffset.value = calculateUpdatedOffset({
              selectedElement: item as HTMLElement,
              containerSize: unref(containerSize),
              contentSize: unref(contentSize),
              currentScrollOffset: unref(scrollOffset),
              isHorizontal: unref(isHorizontal)
            });

            return;
          }
        }
      }
    }

    function onFocusout(event: FocusEvent) {
      isFocused.value = false;
    }

    function onFocus(event: FocusEvent) {
      if (!unref(isFocused) && !(event.relatedTarget && unref(contentRef)?.contains(event.relatedTarget as Node))) {
        focus();
      }
    }

    function onKeydown(event: KeyboardEvent) {
      if (!unref(containerRef)) return;

      event.preventDefault();

      if (unref(isHorizontal)) {
        if (event.key === keyNames.right) {
          focus('next');
        } else if (event.key === keyNames.left) {
          focus('prev');
        }
      } else {
        if (event.key === keyNames.down) {
          focus('next');
        } else if (event.key === keyNames.up) {
          focus('prev');
        }
      }
    }

    function focus(location?: 'next' | 'prev' | 'first' | 'last') {
      if (!unref(contentRef)) return;

      if (!location) {
        const focusable = focusableChildren(unref(contentRef)!);
        focusable[0]?.focus();
      } else if (location === 'next') {
        const el = unref(contentRef)!.querySelector(':focus')?.nextElementSibling as HTMLElement | undefined;

        if (el) {
          el.focus();
        } else {
          focus('first');
        }
      } else if (location === 'prev') {
        const el = unref(contentRef)!.querySelector(':focus')?.previousElementSibling as HTMLElement | undefined;

        if (el) {
          el.focus();
        } else {
          focus('last');
        }
      } else if (location === 'first') {
        (unref(containerRef)!.firstElementChild as HTMLElement)?.focus();
      } else if (location === 'last') {
        (unref(containerRef)!.lastElementChild as HTMLElement)?.focus();
      }
    }

    function scrollTo(location: 'prev' | 'next') {
      const newAbsoluteOffset = unref(scrollOffset) + (location === 'prev' ? -1 : 1) * unref(containerSize);
      scrollOffset.value = clamp(newAbsoluteOffset, 0, unref(contentSize) - unref(containerSize));
    }

    const contentStyles = computed(() => {
      // This adds friction when scrolling the 'wrong' way when at max offset
      let scrollAmount =
        unref(scrollOffset) > unref(contentSize) - unref(containerSize)
          ? -(unref(contentSize) - unref(containerSize)) +
            bias(unref(contentSize) - unref(containerSize) - unref(scrollOffset))
          : -unref(scrollOffset);

      // This adds friction when scrolling the 'wrong' way when at min offset
      if (unref(scrollOffset) <= 0) {
        scrollAmount = bias(-unref(scrollOffset));
      }

      const sign = 1;

      return {
        transform: `translate${unref(isHorizontal) ? 'X' : 'Y'}(${sign * scrollAmount}px)`,
        transition: unref(disableTransition) ? 'none' : '',
        willChange: unref(disableTransition) ? 'transform' : ''
      };
    });

    const slotProps = computed(() => ({
      next: group.next,
      prev: group.prev,
      select: group.select,
      isSelected: group.isSelected
    }));

    const hasAffixes = computed(() => {
      switch (props.showArrows) {
        // Always show arrows on desktop & mobile
        case 'always':
          return true;

        // Always show arrows on desktop
        case 'desktop':
          return !unref(mobile);

        // Show arrows on mobile when overflowing.
        // This matches the default 2.2 behavior
        case true:
          return unref(isOverflowing) || Math.abs(unref(scrollOffset)) > 0;

        // Always show on mobile
        case 'mobile':
          return unref(mobile) || unref(isOverflowing) || Math.abs(unref(scrollOffset)) > 0;

        // Always show arrows when
        // overflowed on desktop
        default:
          return !unref(mobile) && (unref(isOverflowing) || Math.abs(unref(scrollOffset)) > 0);
      }
    });

    const hasPrev = computed(() => Math.abs(unref(scrollOffset)) > 0);
    const hasNext = computed(() => unref(contentSize) > Math.abs(unref(scrollOffset) + unref(containerSize)));

    useRender(() =>
      h(
        props.tag,
        {
          class: classNames('cds-slide-group', {
            'cds-slide-group--vertical': !unref(isHorizontal),
            'cds-slide-group--has-affixes': unref(hasAffixes),
            'cds-slide-group--is-overflowing': unref(isOverflowing)
          }),
          tabindex: unref(isFocused) || unref(group.selected).length ? -1 : 0,
          onFocus
        },
        [
          unref(hasAffixes) &&
            h(
              'div',
              {
                key: 'prev',
                class: classNames('cds-slide-group__prev', {
                  'cds-slide-group__prev--disabled': !unref(hasPrev)
                }),
                onClick: () => scrollTo('prev')
              },
              slots.prev?.(unref(slotProps)) ??
                h(CdsTransitionFade, null, {
                  default: () => h(CdsFunctionalButton, { icon: props.prependIcon, disabled: !unref(hasPrev) })
                })
            ),
          h(
            'div',
            {
              key: 'container',
              ref: containerRef,
              class: 'cds-slide-group__container',
              onScroll
            },
            h(
              'div',
              {
                ref: contentRef,
                class: 'cds-slide-group__content',
                style: unref(contentStyles),
                onTouchstartPassive: onTouchstart,
                onTouchmovePassive: onTouchmove,
                onTouchendPassive: onTouchend,
                onFocusin,
                onFocusout,
                onKeydown
              },
              slots.default?.(unref(slotProps))
            )
          ),
          unref(hasAffixes) &&
            h(
              'div',
              {
                key: 'next',
                class: classNames('cds-slide-group__next', {
                  'cds-slide-group__next--disabled': !unref(hasNext)
                }),
                onClick: () => scrollTo('next')
              },
              slots.next?.(unref(slotProps)) ??
                h(CdsTransitionFade, null, {
                  default: () => h(CdsFunctionalButton, { icon: props.appendIcon, disabled: !unref(hasNext) })
                })
            )
        ]
      )
    );

    return {
      selected: group.selected,
      scrollTo,
      scrollOffset,
      focus
    };
  }
});
</script>

<style lang="scss">
@import './CdsSlideGroup';
</style>
